import React from 'react';

import { resetPassword } from '../Api/Account';
import { FlashMessage } from '../FlashMessage/FlashMessage';
import Button from '../Form/Button/Button';
import MaterialInput from '../Form/MaterialInput/MaterialInput';
import { SaveState } from '../MyLom/Account/EditAccount/Types';
import { ResetPasswordInterface } from './Types';

import './ResetPassword.scss';

enum FailureState {
    NONE,
    FIELD_MISSING,
}

const isKeyInFields = (name: string, fields: ResetPasswordInterface): name is keyof ResetPasswordInterface => {
    return fields.hasOwnProperty(name);
};

export default function ResetPassword() {
    const [fields, updateFields] = React.useState<ResetPasswordInterface>({
        email: '',
    });
    const [saveState, setSaveState] = React.useState<SaveState | null>(null);
    const [validationFailure, setValidationFailure] = React.useState(FailureState.NONE);
    const [serverError, setServerError] = React.useState('');

    function updateField(field: HTMLInputElement) {
        const newFields = { ...fields };
        if (isKeyInFields(field.name, fields)) {
            newFields[field.name] = field.value;
            updateFields(newFields);
        }
    }

    function validate(): boolean {
        if (fields.email === '') {
            setValidationFailure(FailureState.FIELD_MISSING);
            return false;
        }

        setValidationFailure(FailureState.NONE);
        return true;
    }

    function submit(event: React.FormEvent): void {
        event.preventDefault();
        if (validate() === true) {
            setSaveState(SaveState.InProgress);
            resetPassword(
                fields,
                () => {
                    setSaveState(SaveState.Successful);
                    setServerError('');
                    updateFields({
                        email: '',
                    });
                },
                (errorMessage) => {
                    setSaveState(SaveState.Failure);
                    setServerError(errorMessage);
                },
                () => {
                    setSaveState(SaveState.Failure);
                    setServerError('Es gab einen allgemeinen Fehler beim Zurücksetzen Ihres Passworts.');
                },
            );
        }
    }

    function onEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
        updateField(event.target);
    }

    return (
        <div className="reset-password">
            <h3>Passwort vergessen</h3>
            {saveState === SaveState.Successful ? (
                <p>Sie erhalten in Kürze eine E-Mail mit weiteren Informationen</p>
            ) : (
                <ResetForm submit={submit} fields={fields} onChange={onEmailChange} saveState={saveState} />
            )}
            {validationFailure === FailureState.FIELD_MISSING && (
                <p className="reset-password__error-message">Bitte tragen Sie eine Mailadresse ein</p>
            )}
            {serverError !== '' && <p className="reset-password__error-message">{serverError}</p>}
            <FlashMessage visible={saveState === SaveState.Successful}>Zurücksetzung erfolgreich</FlashMessage>
            <FlashMessage visible={saveState === SaveState.Failure} error={true}>
                Zurücksetzung fehlgeschlagen
            </FlashMessage>
        </div>
    );
}

interface ResetFormProps {
    submit: (event: React.FormEvent) => void;
    fields: ResetPasswordInterface;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    saveState: SaveState | null;
}

const ResetForm: React.FunctionComponent<ResetFormProps> = (props) => (
    <form onSubmit={props.submit} className="reset-password-form">
        <div className="row">
            <div className="col-lg-7">
                <MaterialInput
                    name="email"
                    id="reset-password-email"
                    type="email"
                    label="E-Mail Adresse"
                    value={props.fields.email}
                    onChange={props.onChange}
                />
                <Button primary={true} disabled={props.saveState === SaveState.InProgress}>
                    zurücksetzen
                </Button>
            </div>
            <div className="col-lg-5" />
        </div>
    </form>
);
